import React, { use, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { ExtendedCompleteRiskFields } from '@pages-clean/risk-assessment/risk-log/constants';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import styles from '@pages-clean/risk-assessment/risk-log/sections/childArea/styles.module.scss';
import { assumeUtc, getUTCDate } from '@utils-clean/date-time-helpers';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { mapRiskReviewStatusIcon } from '@utils/risk-helper';
import { saveRiskReviewComplete, saveRiskReviewCompleteVariables } from '@queries/generated/risk-assessment.generated';
import { GET_COMPLETE_RISKS_RISK_LOG, SAVE_RISK_REVIEW_COMPLETE } from '@queries/risk-assessment';

interface RiskDeleteDialogProps {
    onClose: () => void;
    onSubmit: () => void;
    risk: ExtendedCompleteRiskFields;
    showNewDatasetWarning?: boolean;
}

type FormInputs = {
    reviewCompleteDate: Date | null;
};

const CompleteRiskReviewDialog = ({ onClose, onSubmit, risk, showNewDatasetWarning }: RiskDeleteDialogProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.reviewComplete' });
    const { t: tCommon } = useTranslation('common');
    const { current } = use(RiskLogContext);
    const [saveRiskReview] = useMutation<saveRiskReviewComplete, saveRiskReviewCompleteVariables>(
        SAVE_RISK_REVIEW_COMPLETE,
    );
    const { study } = current;
    const reviewStatusIcon = mapRiskReviewStatusIcon(risk?.reviewStatus);
    const reviewStatus = risk?.reviewStatus as string;
    const {
        control,
        handleSubmit,
        setValue,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            reviewCompleteDate: null,
        },
    });

    useEffect(() => {
        setValue('reviewCompleteDate', getUTCDate(), { shouldDirty: true, shouldValidate: true });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const onSave = async (data: FormInputs) => {
        saveRiskReview({
            variables: {
                id: risk?.id,
                studyId: study?.id,
                dateLastReviewed: assumeUtc(data.reviewCompleteDate),
            },
            refetchQueries: [{ query: GET_COMPLETE_RISKS_RISK_LOG, variables: { studyId: study?.id } }],
        })
            .then(async (response) => {
                if (!response?.data || !response?.data?.saveRiskReviewComplete) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                onSubmit();
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const getIconColor = (color: string) => {
        switch (color) {
            case 'primary':
                return styles.primary;
            case 'secondary':
                return styles.secondary;
            case 'green':
                return styles.green;
            case 'grey':
                return styles.grey;
            default:
                return styles.grey;
        }
    };

    return (
        <DraggableDialog
            dataTestId="delete-risk-warning"
            onSubmit={() => handleSubmit(onSave)()}
            onClose={onClose}
            title={t('title')}
            initialWidth={450}
            initialHeight={280}
            height={null}
            primaryBtnLabel={tCommon('confirm')}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnLabel={tCommon('cancel')}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                {showNewDatasetWarning && (
                    <InfoField
                        type="Warning"
                        message={
                            <div>
                                <div>{t('newDatasetDataWarning')}</div>
                            </div>
                        }
                    />
                )}
                {risk.dateLastReviewed && (
                    <InfoField
                        type="Warning"
                        message={
                            <div>
                                <div>{t('warning')}</div>
                                <br />
                                <div className="k-font-medium k-text-secondary">
                                    <span>{t('labels.lastReviewDate')}</span>
                                    {' - '}
                                    <span>{stringDateWithoutTimeFromUtc(risk.dateLastReviewed, 'DD-MMM-YYYY')}</span>
                                </div>
                                <div className="k-font-medium k-text-secondary k-d-flex k-align-items-center">
                                    <span>{t('labels.reviewStatus')}</span>
                                    &nbsp;-&nbsp;
                                    <span>{reviewStatus !== '' ? reviewStatus : t('shared.n/a')}</span>
                                    {reviewStatusIcon && reviewStatusIcon.color && (
                                        <FontAwesomeIcon
                                            icon={reviewStatusIcon.icon}
                                            className={getIconColor(reviewStatusIcon.color)}
                                            size="1x"
                                            style={{ marginLeft: '4px' }}
                                        />
                                    )}
                                </div>
                            </div>
                        }
                    />
                )}
                <DatePickerController
                    control={control}
                    name="reviewCompleteDate"
                    label={t('fields.reviewCompleteDate')}
                    data-testid="risk-field-review-complete-date"
                    fillMode="solid"
                    required
                />
            </div>
        </DraggableDialog>
    );
};

export default CompleteRiskReviewDialog;
